import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

export const TICKET_TYPE_SHOW_HIDE_COMPARISONS = ['is', 'is_not'] as const;

const TicketTypeShowHideComparisonValidationSchema = z
  .enum(TICKET_TYPE_SHOW_HIDE_COMPARISONS)
  .openapi({
    description:
      '`is` shows the ticket type while any of `ticketTypeIds` is selected. `is_not` shows it while none of them is.',
    example: 'is',
  });

const RULE_DESCRIPTION =
  'Shows this ticket type only while the customer has (or has not) picked certain other ticket types on the same event, e.g. show "Parking pass" only while "VIP" or "Weekend pass" is selected. Ticket types from any ticket group on the event can be named. ' +
  'This only changes what the checkout displays: it is not a security gate, and the API never rejects an order because of it. ' +
  'A named ticket type that is later deleted, archived or disabled counts as never selected, and the rule stays in force. ' +
  'Sent whole: it replaces the stored rule. Pass `null` or `{ "enabled": false }` to remove it.';

const enabledField = z.boolean().openapi({
  description:
    'Whether the rule is on. `false` removes the rule: the ticket type shows as usual, and `comparison` and `ticketTypeIds` are ignored.',
  example: true,
});

export const TicketTypeShowHideLogicInputValidationSchema = z
  .object({
    enabled: enabledField,
    comparison: TicketTypeShowHideComparisonValidationSchema.default('is'),
    ticketTypeIds: z
      .array(ObjectIdStringValidationSchema)
      .optional()
      .openapi({
        description:
          'IDs of other ticket types on the same event, from `ticketGroups[].ticketTypes[].id`. The rule matches while any of them is selected. A ticket type cannot name itself, and an ID that is not a ticket type on this event is rejected. With `enabled: true` and no IDs the rule hides nothing.',
        example: ['507f1f77bcf86cd799439014'],
      }),
  })
  .openapi('TicketTypeShowHideLogicInput', { description: RULE_DESCRIPTION });

export const EventTicketTypeShowHideLogicInputValidationSchema = z
  .object({
    enabled: enabledField,
    comparison: TicketTypeShowHideComparisonValidationSchema.default('is'),
    ticketTypeIds: z
      .array(z.string().min(1))
      .optional()
      .openapi({
        description:
          'The `key` of each ticket type the rule watches, as set on `ticketGroups[].ticketTypes[].key` in this request (any group). Keys become the new ticket type IDs when the event is saved, and responses return IDs. The rule matches while any of them is selected. A ticket type cannot name its own key, and a value that is not the key of another ticket type in this request is rejected. With `enabled: true` and no keys the rule hides nothing.',
        example: ['vip'],
      }),
  })
  .openapi('EventTicketTypeShowHideLogicInput', { description: RULE_DESCRIPTION });

export const TicketTypeShowHideLogicResponseValidationSchema = z
  .object({
    enabled: z.boolean().openapi({
      description: 'Whether the rule is on. `false` means the ticket type shows as usual.',
    }),
    comparison: TicketTypeShowHideComparisonValidationSchema,
    ticketTypeIds: z.array(z.string()).openapi({
      description:
        'IDs of the ticket types the rule watches. Can include a ticket type that has since been deleted, archived or disabled: it counts as never selected.',
    }),
  })
  .openapi('TicketTypeShowHideLogic', {
    description:
      'Rule that shows this ticket type only while the customer has (or has not) picked certain other ticket types on the same event. Display only: the API never rejects an order because of it.',
  });

export type TicketTypeShowHideLogicInput = z.input<
  typeof TicketTypeShowHideLogicInputValidationSchema
>;
export type TicketTypeShowHideLogicResponse = z.infer<
  typeof TicketTypeShowHideLogicResponseValidationSchema
>;
